import { captureException } from '@sentry/browser'

import type {
    ChargeResponse,
    ChargeUserFunction,
    DisableAdOrder,
    Order,
    PurchaseStatus,
    Topup,
} from '@/modules/in-game-payments'
import { useAppStore } from '@/store/app-store'
import { useNativeAppStore } from '@/store/native-app-store'
import { useUserStore } from '@/store/user-store/user-store'
import { useAPI } from '@/utils/requests/api-plugin'

import { fetchNativePaymentSkus, verifyNativePurchase } from './api'
import {
    getNativePaymentPlatform,
    getNativePaymentSource,
    getNativeProductsBySku,
    getNativePurchaseMessage,
    getNativePurchaseStatus,
    isNativePurchaseProductsPayload,
    isSuccessfulNativePurchaseStatus,
    normalizeNativePurchaseTransaction,
    selectExactNativePaymentPack,
} from './helpers'
import type { NativePaymentMessage, NativePaymentPlatform, NativePurchaseResult } from './types'

const NATIVE_PRODUCTS_RESPONSE_TIMEOUT_MS = 10_000
const NATIVE_PURCHASE_RESPONSE_TIMEOUT_MS = 5 * 60 * 1000

class NativeExactPackUnavailableError extends Error {}

type ExactNativePaymentOrder = Order | DisableAdOrder

export function useNativeExactPackPurchase(fallbackChargeUser: ChargeUserFunction): { chargeUser: ChargeUserFunction } {
    const appStore = useAppStore()
    const nativeAppStore = useNativeAppStore()
    const userStore = useUserStore()
    const api = useAPI()

    function canUseExactNativePack(value: Order | DisableAdOrder | Topup | null): value is ExactNativePaymentOrder {
        if (appStore.isNativeAppEnv) {
            nativeAppStore.init()
        }

        return (
            appStore.isNativeAppEnv &&
            appStore.isPurchaseEnabled &&
            nativeAppStore.isAvailable &&
            getNativePaymentPlatform(appStore.platform) !== undefined &&
            (value?.type === 'order' || value?.type === 'disable-ad-order') &&
            Number.isFinite(value.amount) &&
            (userStore.user?.goldFenecBalance ?? 0) < value.amount
        )
    }

    function chargeUser(value: Order | DisableAdOrder | Topup | null) {
        if (!value) {
            return fallbackChargeUser(value)
        }

        return chargeUserWithNativeExactPack(value)
    }

    async function chargeUserWithNativeExactPack(value: Order | DisableAdOrder | Topup) {
        await userStore.getUser().catch(() => null)

        if (!canUseExactNativePack(value)) {
            return fallbackChargeUser(value)
        }

        return purchaseExactNativePack(value).catch((error) => {
            if (error instanceof NativeExactPackUnavailableError) {
                return fallbackChargeUser(value)
            }

            captureException(error, {
                tags: {
                    paymentProvider: 'native',
                    nativePaymentFlow: 'exact-pack-fast-path',
                },
                extra: {
                    order: value,
                    appVersion: appStore.appVersion,
                    clid: appStore.clid,
                },
            })

            throw error
        })
    }

    async function purchaseExactNativePack(
        order: ExactNativePaymentOrder,
    ): Promise<ChargeResponse<ExactNativePaymentOrder>> {
        const platform = getNativePaymentPlatform(appStore.platform)

        if (!platform) {
            throw new NativeExactPackUnavailableError('Native payment platform is unavailable')
        }

        const exactPack = await getExactNativePaymentPack(order.amount, platform)

        const transaction = await requestNativePurchase(exactPack.sku, order)
        let verificationResult: { transactionId: string; amount: number }

        try {
            verificationResult = await verifyNativePurchase({
                api,
                clid: appStore.clid,
                order,
                packAmount: exactPack.playgamaCurrencyAmount,
                platform,
                transaction,
            })
            confirmPurchase(transaction.transactionId, 'success')
        } catch (error) {
            confirmPurchase(transaction.transactionId, 'failed')
            throw error
        }

        if (order.type === 'disable-ad-order') {
            return completeDisableAdOrder(order)
        }

        return {
            data: {
                status: 'PAID',
                orderId: verificationResult.transactionId,
                amount: verificationResult.amount,
            },
            errors: [],
            originalResponse: null as unknown as Response,
        }
    }

    async function completeDisableAdOrder(order: DisableAdOrder): Promise<ChargeResponse<DisableAdOrder>> {
        const { amount, durationInDays, externalId, attributes, subscription } = order
        const result = await api.request<PurchaseStatus>('/api/v1/user/disable-ad', {
            method: 'POST',
            body: JSON.stringify({ order: { amount, durationInDays, externalId, attributes, subscription } }),
        })

        if (result.errors.length > 0) {
            throw new Error(result.errors.join(', '))
        }

        await userStore.fetchUserInfo()

        return result
    }

    async function getExactNativePaymentPack(amount: number, platform: NativePaymentPlatform) {
        const skus = await fetchNativePaymentSkus(api, platform)
        const exactSkus = skus.filter((sku) => sku.playgamaCurrencyAmount === amount)

        if (exactSkus.length === 0) {
            throw new NativeExactPackUnavailableError('No exact native payment pack is available')
        }

        const products = await requestNativeProducts(exactSkus.map((sku) => sku.sku))
        const pack = selectExactNativePaymentPack({ amount, platform, products, skus: exactSkus })

        if (!pack) {
            throw new NativeExactPackUnavailableError('No exact native payment product is available')
        }

        return pack
    }

    async function requestNativeProducts(skus: string[]) {
        const productsPromise = waitForNativeMessage(
            getNativeProductsFromMessage,
            NATIVE_PRODUCTS_RESPONSE_TIMEOUT_MS,
            'Native app did not return payment products',
        )

        nativeAppStore.postMessage('purchase.getProducts', { skus })

        return getNativeProductsBySku(await productsPromise)
    }

    async function requestNativePurchase(sku: string, order: ExactNativePaymentOrder) {
        const purchaseResultPromise = waitForNativeMessage(
            getNativePurchaseResultFromMessage,
            NATIVE_PURCHASE_RESPONSE_TIMEOUT_MS,
            'Native app did not return purchase result',
        )

        nativeAppStore.postMessage('purchase.request', {
            sku,
            context: {
                source: getNativePaymentSource(order),
                gameId: order.type === 'order' ? order.gameHru : null,
            },
        })

        const result = await purchaseResultPromise
        const status = getNativePurchaseStatus(result)
        const transaction = normalizeNativePurchaseTransaction(result)

        if (isSuccessfulNativePurchaseStatus(status)) {
            if (!transaction) {
                throw new Error(
                    getNativePurchaseMessage(result) || 'Native payment failed: transaction data is incomplete',
                )
            }

            return transaction
        }

        throw new Error(getNativePurchaseMessage(result) || 'Native payment did not return a purchased transaction')
    }

    function waitForNativeMessage<T>(
        getter: (data: unknown) => T | undefined,
        timeoutMs: number,
        timeoutMessage: string,
    ) {
        return new Promise<T>((resolve, reject) => {
            let unsubscribe = () => {}
            const timeoutId = setTimeout(() => {
                unsubscribe()
                reject(new NativeExactPackUnavailableError(timeoutMessage))
            }, timeoutMs)

            unsubscribe = nativeAppStore.addMessageHandler((data) => {
                const value = getter(data)

                if (value === undefined) {
                    return
                }

                clearTimeout(timeoutId)
                unsubscribe()
                resolve(value)
            })
        })
    }

    function getNativeProductsFromMessage(data: unknown) {
        const message = data as NativePaymentMessage
        const { payload } = message

        if (message.type !== 'native:purchase.products' || !isNativePurchaseProductsPayload(payload)) {
            return undefined
        }

        return payload.products
    }

    function getNativePurchaseResultFromMessage(data: unknown) {
        const message = data as NativePaymentMessage

        if (message.type !== 'native:purchase.result') {
            return undefined
        }

        const result = message.payload as NativePurchaseResult | undefined
        const status = getNativePurchaseStatus(result)

        return status === 'pending' ? undefined : result
    }

    function confirmPurchase(transactionId: string, status: 'success' | 'failed') {
        nativeAppStore.postMessage('purchase.confirm', { transactionId, status })
    }

    return {
        chargeUser: chargeUser as ChargeUserFunction,
    }
}
